/**
 * scheduleUtils — pure scheduling helper functions for the public menu.
 * All functions are stateless; pass restaurant + scheduling config as arguments.
 *
 * BUFFER RULE:
 *   min_anticipation_hours controls the lead-time before a slot can be selected.
 *   Default is 0 (no buffer) — if unconfigured, slots start at opening time.
 *   Set explicitly in scheduling_config to add transparency. Restaurants configure
 *   this in Settings → Horários.
 */

/** Build "HH:MM-HH:MM" label for a slot starting at `curMin` minutes since midnight. */
function _buildSlot(curMin, interval) {
  const fromH = String(Math.floor(curMin / 60)).padStart(2, "0");
  const fromM = String(curMin % 60).padStart(2, "0");
  const toMin = curMin + interval;
  const toH   = String(Math.floor(toMin / 60)).padStart(2, "0");
  const toM   = String(toMin % 60).padStart(2, "0");
  return `${fromH}:${fromM}-${toH}:${toM}`;
}

/** Local YYYY-MM-DD for a Date object (avoids UTC-offset bugs from toISOString). */
function _localDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Generate slots for a single business-hours window.
 * @param {number} startMin  - window open  (minutes since midnight)
 * @param {number} endMin    - window close (minutes since midnight)
 * @param {number} interval  - slot length in minutes
 * @param {boolean} isToday  - whether we're filtering by minTime
 * @param {Date}   minTime   - earliest allowed slot start (only used when isToday)
 * @param {Date}   date      - the calendar date being processed
 * @param {boolean} countOnly - return count placeholders instead of formatted strings
 */
function _slotsForWindow(startMin, endMin, interval, isToday, minTime, date, countOnly) {
  const slots = [];
  let cur = startMin;
  while (cur + interval <= endMin) {
    if (isToday) {
      const slotDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(),
                                Math.floor(cur / 60), cur % 60);
      if (slotDate <= minTime) { cur += interval; continue; }
    }
    slots.push(countOnly ? "x" : _buildSlot(cur, interval));
    cur += interval;
  }
  return slots;
}

const _JS_DAYS = ["domingo","lunes","martes","miercoles","jueves","viernes","sabado"];

export function getServicePeriodRule(sc, dayName, time, channel = "pickup") {
  const period = Number(String(time).split(":")[0]) < 17 ? "lunch" : "dinner";
  const periodRule = sc?.service_period_rules?.[dayName]?.[period] || {};
  const mode = ["normal", "advance_only", "closed"].includes(periodRule[channel])
    ? periodRule[channel]
    : "normal";
  return {
    mode,
    minAdvanceDays: Math.max(1, Number(periodRule.min_advance_days || 1)),
    period,
  };
}

export function isServicePeriodSelectable(sc, dateStr, time, channel = "pickup", now = new Date()) {
  const date = new Date(`${dateStr}T00:00:00`);
  const dayName = _JS_DAYS[date.getDay()];
  const rule = getServicePeriodRule(sc, dayName, time, channel);
  if (rule.mode === "closed") return false;
  if (rule.mode !== "advance_only") return true;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const calendarDays = Math.round((target - today) / 86400000);
  return calendarDays >= rule.minAdvanceDays;
}

export function hasAdvanceServiceRule(sc, channel = "pickup") {
  return Object.values(sc?.service_period_rules || {}).some(day =>
    Object.values(day || {}).some(period => period?.[channel] === "advance_only")
  );
}

/**
 * Returns available dates for scheduling based on business hours.
 * @param {object} restaurant
 * @param {object} sc - scheduling_config
 * @param {Date}   [now] - injectable for testing; defaults to new Date()
 * @returns {Array<{value: string, label: string}>}
 */
export function getAvailableDates(restaurant, sc, now = new Date(), channel = "pickup") {
  const schedulingAgendaDays = sc?.agenda_days || 14;
  const dates = [];

  for (let i = 0; i < schedulingAgendaDays; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    const dateStr = _localDateStr(d);
    const dayName = _JS_DAYS[d.getDay()];
    const bh = restaurant?.business_hours?.find(h => h.day === dayName);
    const dateFormatted = d.toLocaleDateString("pt-BR", { day: "numeric", month: "short" });

    let label;
    if (i === 0) {
      const todaySlots = getTimeSlotsForDate(restaurant, sc, dateStr, now, channel);
      if (todaySlots.length === 0) continue;
      label = `Hoje (${dateFormatted})`;
    } else if (i === 1) {
      label = `Amanha (${dateFormatted})`;
    } else {
      label = d.toLocaleDateString("pt-BR", { weekday: "short", day: "numeric", month: "short" });
    }

    const noConfiguredHours = !restaurant?.business_hours?.length;
    if (noConfiguredHours || (bh?.enabled && bh?.slots?.some(s => s[0] && s[1]))) {
      dates.push({ value: dateStr, label });
    }
  }

  return dates;
}

/**
 * Returns count-only slots for a date (used to check if a day has any slots at all).
 * @param {Date} [now] - injectable for testing; defaults to new Date()
 */
export function getTimeSlotsForDate(restaurant, sc, dateStr, now = new Date(), channel = "pickup") {
  if (!dateStr || dateStr === "asap") return [];

  const interval      = sc?.interval_minutes || 30;
  // ?? 0: no implicit buffer — restaurants configure lead-time explicitly (min_anticipation_hours)
  const anticipation  = (sc?.min_anticipation_hours ?? 0);
  const hoursMode     = sc?.hours_mode || "business_hours";

  const date    = new Date(dateStr);
  const dayName = _JS_DAYS[date.getDay()];
  const bh      = restaurant?.business_hours?.find(h => h.day === dayName);
  if (!bh || !bh.enabled) return [];

  const isToday = dateStr === _localDateStr(now);
  const minTime = new Date(now.getTime() + anticipation * 3_600_000);

  if (hoursMode === "business_hours") {
    const slots = [];
    for (const [start, end] of (bh.slots || [])) {
      if (!start || !end) continue;
      const [sh, sm] = start.split(":").map(Number);
      const [eh, em] = end.split(":").map(Number);
      const generated = _slotsForWindow(sh * 60 + sm, eh * 60 + em, interval, isToday, minTime, date, true);
      if (isServicePeriodSelectable(sc, dateStr, start, channel, now)) slots.push(...generated);
    }
    return slots;
  }

  // Custom / 24-hour mode
  return _slotsForWindow(0, 1440, interval, isToday, minTime, date, true);
}

/**
 * Returns formatted time slot strings for a given date.
 * @param {Date} [now] - injectable for testing; defaults to new Date()
 * @returns {Array<string>} e.g. ["20:00-20:30", "20:30-21:00", ...]
 */
export function getTimeSlots(restaurant, sc, dateStr, now = new Date(), channel = "pickup") {
  if (!dateStr || dateStr === "asap") return [];

  const interval     = sc?.interval_minutes || 30;
  // ?? 0: no implicit buffer — restaurants configure lead-time explicitly
  const anticipation = (sc?.min_anticipation_hours ?? 0);
  const hoursMode    = sc?.hours_mode || "business_hours";

  const date    = new Date(dateStr);
  const dayName = _JS_DAYS[date.getDay()];
  const bh      = restaurant?.business_hours?.find(h => h.day === dayName);
  if (!bh || !bh.enabled) return [];

  const isToday = dateStr === _localDateStr(now);
  const minTime = new Date(now.getTime() + anticipation * 3_600_000);

  if (hoursMode === "business_hours") {
    const slots = [];
    for (const [start, end] of (bh.slots || [])) {
      if (!start || !end) continue;
      const [sh, sm] = start.split(":").map(Number);
      const [eh, em] = end.split(":").map(Number);
      const generated = _slotsForWindow(sh * 60 + sm, eh * 60 + em, interval, isToday, minTime, date, false);
      slots.push(...generated.filter(slot => isServicePeriodSelectable(sc, dateStr, slot.split("-")[0], channel, now)));
    }
    return slots;
  }

  // Custom / 24-hour mode
  return _slotsForWindow(0, 1440, interval, isToday, minTime, date, false);
}
